import { datadogLogs, HandlerType } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { logTransports } from '../logger';
import { datadogTransport } from '../logger/datadogTransport';
import { shouldSuppressAnalyticsEvent } from './analyticsNoise';
// User frustration detection for Datadog Gen2
class DatadogFrustrationDetector {
    config;
    frustrationEvents = [];
    FRUSTRATION_THRESHOLD = 3; // Number of events to trigger frustration
    FRUSTRATION_WINDOW = 5000; // Time window in ms
    constructor(config) {
        this.config = config;
        if (config.trackFrustrations) {
            this.setupFrustrationDetection();
        }
    }
    setupFrustrationDetection() {
        // Track rapid clicks (potential frustration)
        let clickCount = 0;
        let lastClickTime = 0;
        document.addEventListener('click', () => {
            const now = Date.now();
            if (now - lastClickTime < 1000) {
                // Rapid clicks within 1 second
                clickCount++;
                if (clickCount >= 3) {
                    this.recordFrustrationEvent('rapid_clicks');
                    clickCount = 0;
                }
            }
            else {
                clickCount = 1;
            }
            lastClickTime = now;
        });
        // Track form errors (potential frustration)
        document.addEventListener('invalid', () => {
            this.recordFrustrationEvent('form_validation_error');
        });
        // Track 404 errors (potential frustration)
        window.addEventListener('error', (event) => {
            if (event.message.includes('404') || event.message.includes('Not Found')) {
                this.recordFrustrationEvent('page_not_found');
            }
        });
        // Track network errors (potential frustration)
        window.addEventListener('unhandledrejection', (event) => {
            if (event.reason && typeof event.reason === 'object' && 'status' in event.reason) {
                const status = event.reason.status;
                if (status >= 400) {
                    this.recordFrustrationEvent('network_error');
                }
            }
        });
    }
    recordFrustrationEvent(type) {
        const now = Date.now();
        this.frustrationEvents.push({ type, timestamp: now });
        // Clean old events outside the window
        this.frustrationEvents = this.frustrationEvents.filter((event) => now - event.timestamp < this.FRUSTRATION_WINDOW);
        // Check if we have enough events to trigger frustration
        if (this.frustrationEvents.length >= this.FRUSTRATION_THRESHOLD) {
            this.triggerFrustrationDetection();
        }
    }
    triggerFrustrationDetection() {
        const frustrationData = {
            frustrationType: 'user_frustration_detected',
            frustrationEvents: this.frustrationEvents.map((e) => e.type),
            frustrationCount: this.frustrationEvents.length,
            pageUrl: window.location.href,
            timestamp: new Date().toISOString(),
        };
        // Send frustration event to Datadog
        if (typeof datadogRum !== 'undefined') {
            datadogRum.addAction('User Frustration Detected', frustrationData);
        }
        // Clear events after reporting
        this.frustrationEvents = [];
    }
}
export function datadogReporter(info, config) {
    const isLocalhost = window.location.hostname === 'localhost';
    const logsEnabled = config.logsEnabled ?? true;
    const rumEnabled = config.rumEnabled ?? true;
    // Don't forward error logs by default, do enable the log transport by default
    // forwardErrorsToLogs incorrectly is called forwardConsoleLogs, this is for backwards compatibility
    const forwardErrorsToLogs = config.forwardConsoleLogs ?? false;
    const enableLogTransport = config.logTransport !== false;
    // Only init datadog logs if something is using it.
    if (logsEnabled && (forwardErrorsToLogs === true || enableLogTransport === true)) {
        datadogLogs.init({
            site: config.site,
            clientToken: config.clientToken,
            service: info.service,
            env: info.environment,
            version: config.version ?? info.version,
            beforeSend: config.beforeLogsSend,
            usePartitionedCrossSiteSessionCookie: config.usePartitionedCrossSiteSessionCookie,
            useSecureSessionCookie: config.useSecureSessionCookie ?? !isLocalhost,
            // Note: useCrossSiteSessionCookie is not available in newer Datadog versions
            trackSessionAcrossSubdomains: config.trackSessionAcrossSubdomains,
            forwardErrorsToLogs,
            allowedTrackingOrigins: config.allowedTrackingOrigins,
        });
        datadogLogs.logger.setHandler(HandlerType.http);
    }
    // Add the datadog log transport
    if (logsEnabled && enableLogTransport) {
        logTransports.push(datadogTransport(typeof config.logTransport === 'boolean' ? {} : config.logTransport));
    }
    if (rumEnabled) {
        datadogRum.init({
            enableExperimentalFeatures: ['feature_flags'],
            site: config.site,
            clientToken: config.clientToken,
            applicationId: config.applicationId,
            service: info.service,
            env: info.environment,
            version: config.version ?? info.version,
            usePartitionedCrossSiteSessionCookie: config.usePartitionedCrossSiteSessionCookie,
            // Use correct Datadog v6 SDK options
            sessionSampleRate: config.sessionSampleRate ?? config.rumSampleRate ?? config.sampleRate ?? 100,
            sessionReplaySampleRate: config.sessionReplaySampleRate ?? config.replaySampleRate ?? 100,
            // Track interactions (Note: trackFrustrations is not available in Datadog v6)
            trackUserInteractions: config.trackUserInteractions ?? config.trackInteractions ?? false,
            useSecureSessionCookie: config.useSecureSessionCookie ?? !isLocalhost,
            // Note: useCrossSiteSessionCookie is not available in newer Datadog versions
            trackSessionAcrossSubdomains: config.trackSessionAcrossSubdomains,
            trackViewsManually: config.trackViewsManually ?? false,
            actionNameAttribute: config.actionNameAttribute ?? 'data-analytics-name',
            beforeSend: config.beforeSend,
            defaultPrivacyLevel: config.defaultPrivacyLevel ?? 'mask-user-input',
            allowedTracingUrls: config.allowedTracingUrls ?? config.allowedTrackingOrigins,
            excludedActivityUrls: config.excludedActivityUrls,
        });
        // Initialize frustration detector
        new DatadogFrustrationDetector(config);
    }
    function normalizeErrorForLogs(error) {
        if (error instanceof Error) {
            return {
                message: error.message,
                name: error.name,
                stack: error.stack,
            };
        }
        return {
            message: String(error),
        };
    }
    const reporter = {
        trackEvent: function (event) {
            if (!rumEnabled) {
                return;
            }
            if (shouldSuppressAnalyticsEvent(event.message, config.ignoreTrackEventPatterns)) {
                return;
            }
            datadogRum.addAction(event.message, {
                level: event.level,
                ...event.metadata,
                ...event.tags,
                ...event.metrics,
            });
        },
        addBreadcrumb: function (breadcrumb) {
            if (!rumEnabled) {
                return;
            }
            datadogRum.addAction(breadcrumb.message, {
                ...breadcrumb.metadata,
                category: breadcrumb.category,
            });
        },
        addMetadata: function (metadata) {
            for (const [key, value] of Object.entries(metadata)) {
                if (value !== null) {
                    if (rumEnabled) {
                        datadogRum.setGlobalContextProperty(key, value);
                    }
                    if (logsEnabled) {
                        // Note, this will add duplicate context data in logs.
                        // But this is valuable for logs ingested outside of the browser logger.
                        datadogLogs.setGlobalContextProperty(key, value);
                    }
                }
                else {
                    if (rumEnabled) {
                        datadogRum.removeGlobalContextProperty(key);
                    }
                    if (logsEnabled) {
                        // But this is valuable for logs ingested outside of the browser logger.
                        datadogLogs.removeGlobalContextProperty(key);
                    }
                }
            }
        },
        setUser: function (user) {
            if (!rumEnabled) {
                return;
            }
            if (user) {
                datadogRum.setUser({
                    ...user,
                    id: user.id,
                    email: user.email,
                    name: user.name ?? user.email,
                });
            }
            else {
                datadogRum.setUser({});
            }
        },
        setRouteName: function (routeName) {
            if (!rumEnabled) {
                return;
            }
            reporter.addMetadata({ routeName });
        },
        setPageName: function (pageName) {
            if (!rumEnabled) {
                return;
            }
            if (config.trackViewsManually) {
                datadogRum.startView(pageName);
            }
            else {
                reporter.addMetadata({ pageName });
            }
        },
        reportError: function (error, metadata) {
            if (rumEnabled) {
                datadogRum.addError(error, metadata);
                return;
            }
            if (logsEnabled) {
                const normalized = normalizeErrorForLogs(error);
                datadogLogs.logger.error(normalized.message, {
                    ...metadata,
                    errorName: normalized.name,
                    errorStack: normalized.stack,
                });
            }
        },
        reportFeatureFlag: function (flag) {
            if (!rumEnabled) {
                return;
            }
            datadogRum.addFeatureFlagEvaluation(flag.name, flag.value);
        },
        recordSession: function () {
            if (!rumEnabled) {
                return;
            }
            datadogRum.startSessionReplayRecording();
        },
        recordSessionStop: function () {
            if (!rumEnabled) {
                return;
            }
            datadogRum.stopSessionReplayRecording();
        },
    };
    return reporter;
}
//# sourceMappingURL=datadogReporter.js.map